import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import Ct2LearnMore from './ct2_learn_more';
import { displayVAT } from 'utilities/currency';
import { MarketActionCard, MarketList, MarketRow } from '@market/react';
import { getLocalePath } from 'utilities/locales';
import InfoBubble from 'components/common/info_bubble';
import TagIcon from '@square-icons/react/16/UI/tag';
import {
  allProductsAreColorGrouped,
  createColorProductFromProduct,
  filterInvalidColorProductConfigs,
} from 'typescript/utilities/color_group_utils';
import ColorProductGroup from 'typescript/components/product_groups/color_product_group';

const NO_ADD_ON_VALUE = '0';

const YesOption = React.memo(
  ({
    addOn,
    variantId,
    component,
    onSelected = () => {},
    onDeselected = () => {},
  }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [isComponentVisible, setIsComponentVisible] = useState(false);

    return (
      <MarketActionCard
        value={variantId.toString()}
        onMarketCardSelected={() => {
          setIsComponentVisible(true);
          onSelected();
        }}
        onMarketCardDeselected={() => {
          setIsComponentVisible(false);
          onDeselected();
        }}
      >
        <MarketRow>
          <p slot="label">{t('productPage.addAddOn', { name: addOn.name })}</p>
          <p
            slot="side-label"
            className="medium-30"
            style={{ whiteSpace: 'nowrap' }}
          >
            {addOn.sale_price_unformatted > 0 && '+'}
            {addOn.sale_price}
            {displayVAT() && addOn.sale_price_unformatted > 0 && (
              <span
                style={{
                  fontSize: theme.type.fontSize.tiny,
                  marginLeft: theme.spacing(1 / 2),
                }}
              >
                {t('common.plusVAT')}
              </span>
            )}
          </p>
          {isComponentVisible && component}
        </MarketRow>
      </MarketActionCard>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.addOn === nextProps.addOn &&
      prevProps.variantId === nextProps.variantId &&
      prevProps.component === nextProps.component
    );
  },
);
YesOption.displayName = 'YesOption';

const ProductAddOn = (props) => {
  const {
    category,
    addOns,
    selected = NO_ADD_ON_VALUE,
    onSelect = () => {},
    yesAsFirstOption = false, // if we want it to be yes/no vs no/yes
    currentProduct,
  } = props;

  const theme = useTheme();
  const { t, i18n } = useTranslation();

  if (addOns.length === 0) {
    return null;
  }

  const renderAddonSubtext = () => {
    const linkKey = `relations.addOn.subtext.${category}`;

    return i18n.exists(linkKey) ? (
      <p
        dangerouslySetInnerHTML={{
          __html: t(linkKey, { locale: getLocalePath() }),
        }}
        className="paragraph-20"
        style={{ marginTop: theme.spacing(1) }}
      />
    ) : null;
  };

  const NoOption = (
    <MarketActionCard key="no-option" value={NO_ADD_ON_VALUE}>
      <MarketRow>
        <p slot="label">{t(`relations.addOn.noText.${category}`)}</p>
      </MarketRow>
    </MarketActionCard>
  );

  const ColorProductGroupYesOption = React.memo(({ addOns }) => {
    const validColorProducts = filterInvalidColorProductConfigs(
      addOns.map((addOn) => createColorProductFromProduct(addOn)),
    );

    if (validColorProducts.length === 0) {
      return null;
    }

    const [selectedColorProduct, setSelectedColorProduct] = useState(
      validColorProducts[0], // Default to the first product
    );

    const selectedAddOn = addOns.find(
      (addOn) => addOn.variant_id === selectedColorProduct.variantId,
    );

    return (
      <YesOption
        addOn={selectedAddOn}
        variantId={selectedAddOn.variant_id}
        component={
          <ColorProductGroup
            titleOverride=" "
            selectedProduct={selectedColorProduct}
            products={validColorProducts}
            onProductSelected={(colorProduct) => {
              // We want to change the selected product when the user selects a different color
              setSelectedColorProduct(colorProduct);
              // When the color is changed, we should bubble this up for the variant to be added to the cart later on.
              onSelect({
                category,
                newVariantId: colorProduct.variantId,
                choice: `${colorProduct.name} - ${colorProduct.displayName}`,
              });
            }}
          />
        }
        onSelected={() => {
          // When the yes option is selected, we should default to the first product
          setSelectedColorProduct(validColorProducts[0]);
        }}
      />
    );
  });
  ColorProductGroupYesOption.displayName = 'ColorProductGroupYesOption';

  const YesOptions = useMemo(() => {
    /* Check for Product Groups */
    if (allProductsAreColorGrouped(addOns)) {
      return (
        <ColorProductGroupYesOption
          key="yes-color-group-option"
          addOns={addOns}
        />
      );
    }

    /* If not a product group, render as normal */
    return addOns.map((addOn) => {
      const { variant_id } = addOn;

      return (
        <YesOption
          key={`yes-option-${variant_id}`}
          addOn={addOn}
          variantId={variant_id}
        />
      );
    });
  }, [addOns]);

  const renderAddons = () => {
    const promotionBanners = addOns
      .map((addOn) =>
        addOn.promotion_banner ? (
          <div
            key={addOn.variant_id.toString()}
            className="medium-30"
            style={{ margin: theme.spacing(2, 0) }}
          >
            <InfoBubble spacing={2} icon={<TagIcon />}>
              <p className="medium-30">
                {t(
                  `relations.addOn.promotionBanner.${currentProduct.base_product_key}.${addOn.base_product_key}`,
                )}
              </p>
            </InfoBubble>
          </div>
        ) : null,
      )
      .filter(Boolean);

    const options = yesAsFirstOption
      ? [promotionBanners, YesOptions, NoOption]
      : [promotionBanners, NoOption, YesOptions];

    return options.flat();
  };

  return (
    <div key={category}>
      <p className="medium-30" style={{ margin: theme.spacing(2, 0) }}>
        {t(`relations.addOn.label.${category}`)}
      </p>
      <MarketList
        interactive
        value={selected}
        onMarketListSelectionsDidChange={(event) => {
          const {
            detail: { newSelection, newSelectionValue },
            target,
          } = event;

          onSelect({
            category: target.dataset.category,
            newVariantId: newSelectionValue,
            choice: newSelection.innerText,
          });
        }}
        data-category={category}
        data-testid="add-on-list"
      >
        {renderAddons()}
        {renderAddonSubtext()}
      </MarketList>
      {category === 'connected_terminal' && <Ct2LearnMore />}
    </div>
  );
};

export { ProductAddOn as default, NO_ADD_ON_VALUE };
